export const BASE = (import.meta.env.VITE_API_URL ?? '') + '/api'

function getToken() {
  return localStorage.getItem('bst-token') ?? ''
}

let refreshPromise: Promise<string | null> | null = null

async function tryRefresh(): Promise<string | null> {
  const refreshToken = localStorage.getItem('bst-refresh')
  if (!refreshToken) return null
  try {
    const res = await fetch(`${BASE}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    })
    if (!res.ok) return null
    const data = await res.json() as { accessToken?: string; refreshToken?: string }
    if (!data.accessToken) return null
    localStorage.setItem('bst-token', data.accessToken)
    if (data.refreshToken) localStorage.setItem('bst-refresh', data.refreshToken)
    return data.accessToken
  } catch {
    return null
  }
}

// Ensures a valid (non-expired) token is present; refreshes if possible.
// Use this before raw streaming fetch calls that bypass apiFetch.
export async function ensureToken(): Promise<string> {
  const token = localStorage.getItem('bst-token') ?? ''
  if (!token) {
    if (!refreshPromise) refreshPromise = tryRefresh().finally(() => { refreshPromise = null })
    return (await refreshPromise) ?? ''
  }
  // Decode JWT exp without a library
  try {
    const payload = JSON.parse(atob(token.split('.')[1]))
    if (typeof payload.exp === 'number' && payload.exp * 1000 < Date.now() + 30_000) {
      if (!refreshPromise) refreshPromise = tryRefresh().finally(() => { refreshPromise = null })
      return (await refreshPromise) ?? token
    }
  } catch { /* malformed — send as-is */ }
  return token
}

async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const reqHeaders = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${getToken()}`,
    ...(init.headers ?? {}),
  }
  let res: Response
  try {
    res = await fetch(`${BASE}${path}`, { ...init, headers: reqHeaders })
  } catch (err) {
    if (err instanceof TypeError) throw new Error('Нет соединения с сервером. Проверьте интернет и попробуйте снова.')
    throw err
  }
  if (res.status === 204) return undefined as T
  if (res.status === 401) {
    if (!refreshPromise) refreshPromise = tryRefresh().finally(() => { refreshPromise = null })
    const newToken = await refreshPromise
    if (newToken) {
      const retried = await fetch(`${BASE}${path}`, {
        ...init,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${newToken}`,
          ...(init.headers ?? {}),
        },
      })
      if (retried.status === 204) return undefined as T
      if (retried.status !== 401) {
        const ct = retried.headers.get('content-type') ?? ''
        if (!ct.includes('application/json') && !ct.includes('application/problem')) {
          throw new Error(retried.ok ? 'API недоступен.' : `HTTP ${retried.status}`)
        }
        const d = await retried.json() as T
        if (!retried.ok) {
          const m = (d as Record<string, unknown>).message ?? `HTTP ${retried.status}`
          throw new Error(Array.isArray(m) ? (m as string[]).join('. ') : String(m))
        }
        return d
      }
    }
    localStorage.removeItem('bst-auth')
    localStorage.removeItem('bst-token')
    localStorage.removeItem('bst-refresh')
    window.location.replace('/login')
    throw new Error('Сессия истекла. Войдите снова.')
  }
  const contentType = res.headers.get('content-type') ?? ''
  if (!contentType.includes('application/json') && !contentType.includes('application/problem')) {
    throw new Error(res.ok
      ? 'API недоступен. Убедитесь, что сервер запущен.'
      : `HTTP ${res.status}`)
  }
  const data = await res.json() as T
  if (!res.ok) {
    const msg = (data as Record<string, unknown>).message ?? `HTTP ${res.status}`
    throw new Error(Array.isArray(msg) ? (msg as string[]).join('. ') : String(msg))
  }
  return data
}

export const api = {
  get: <T>(path: string) => apiFetch<T>(path),
  post: <T>(path: string, body?: unknown) =>
    apiFetch<T>(path, { method: 'POST', body: JSON.stringify(body) }),
  patch: <T>(path: string, body?: unknown) =>
    apiFetch<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
  put: <T>(path: string, body?: unknown) =>
    apiFetch<T>(path, { method: 'PUT', body: JSON.stringify(body) }),
  delete: <T = void>(path: string) =>
    apiFetch<T>(path, { method: 'DELETE' }),
}

// ── §80 Product Telemetry ─────────────────────────────────────────────────────

export function trackEvent(event: string, props?: Record<string, unknown>): void {
  apiFetch<void>('/telemetry/event', {
    method: 'POST',
    body: JSON.stringify({ event, props }),
  }).catch(() => null)
}

// ── Sites ─────────────────────────────────────────────────────────────────────

export interface ApiSite {
  id: string
  name: string
  slug: string
  favicon?: string
  gaId?: string
  ymId?: string
  fbPixelId?: string
  vkPixelId?: string
  gtmId?: string
  themeConfig?: string
  customHeadHtml?: string
  customBodyHtml?: string
  formWebhookUrl?: string
  formEmailTo?: string
  customDomain?: string
  customDomainVerified?: boolean
  globalHeader?: string
  globalFooter?: string
  cookieConsent?: boolean
  cookieConsentText?: string
  mailchimpApiKey?: string
  mailchimpListId?: string
  unisenderApiKey?: string
  unisenderListId?: string
  sendpulseClientId?: string
  sendpulseClientSecret?: string
  sendpulseListId?: string
  bitrix24WebhookUrl?: string
  amoCrmDomain?: string
  amoCrmToken?: string
  getCourseDomain?: string
  smsRuApiKey?: string
  smsOwnerPhone?: string
  formTelegramChatId?: string
  whatsappNumber?: string
  telegramUsername?: string
  vkGroupId?: string
  callbackPhone?: string
  jivoChatId?: string
  crispWebsiteId?: string
  tawkPropertyId?: string
  tawkWidgetId?: string
  recaptchaSiteKey?: string
  socialProofEnabled?: boolean
  socialProofMessages?: string
  backToTop?: boolean
  scrollProgress?: boolean
  scrollProgressColor?: string
  pagePreloader?: boolean
  customCursor?: boolean
  customCursorColor?: string
  pageTransition?: string
  siteLanguage?: string
  pwaEnabled?: boolean
  pwaName?: string
  pwaShortName?: string
  pwaThemeColor?: string
  pwaBackgroundColor?: string
  publiclyListed?: boolean
  publicListingTags?: string[]
  sitePasswordProtected?: boolean
  uploadedHtml?: string
  status: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED'
  publishedAt?: string
  updatedAt?: string
  createdAt?: string
  pages: ApiPage[]
}

export interface ApiPageMeta {
  id: string
  title: string
  slug: string
  isHome: boolean
  seoTitle?: string
  seoDesc?: string
  updatedAt: string
  cmsTemplateFor?: string
}

export interface ApiPage {
  id: string
  title: string
  slug: string
  isHome: boolean
  seoTitle?: string
  seoDesc?: string
  ogImage?: string
  structuredData?: string
  pageCss?: string
  pageJs?: string
  robotsIndex?: boolean
  canonicalUrl?: string
  content: { sections?: unknown[] }
  updatedAt: string
  cmsTemplateFor?: string
}

// Site with pages metadata only (no page content / sections)
export type ApiSiteMetadata = Omit<ApiSite, 'pages'> & { pages: ApiPageMeta[] }

export const sitesApi = {
  list: () => api.get<ApiSite[]>('/sites'),
  create: (name: string, slug: string) => api.post<ApiSite>('/sites', { name, slug }),
  get: (id: string) => api.get<ApiSite>(`/sites/${id}`),
  getMetadata: (id: string) => api.get<ApiSiteMetadata>(`/sites/${id}?pagesContent=false`),
  update: (id: string, data: Partial<{ name: string; slug: string; favicon: string; gaId: string; ymId: string; fbPixelId: string; vkPixelId: string; gtmId: string; themeConfig: string; customHeadHtml: string; customBodyHtml: string; published: boolean; formWebhookUrl: string; formEmailTo: string; customDomain: string; globalHeader: string; globalFooter: string; cookieConsent: boolean; cookieConsentText: string; mailchimpApiKey: string; mailchimpListId: string; unisenderApiKey: string; unisenderListId: string; sendpulseClientId: string; sendpulseClientSecret: string; sendpulseListId: string; bitrix24WebhookUrl: string; amoCrmDomain: string; amoCrmToken: string; getCourseDomain: string; smsRuApiKey: string; smsOwnerPhone: string; formTelegramChatId: string; whatsappNumber: string; telegramUsername: string; vkGroupId: string; callbackPhone: string; jivoChatId: string; crispWebsiteId: string; tawkPropertyId: string; tawkWidgetId: string; recaptchaSiteKey: string; recaptchaSecretKey: string; socialProofEnabled: boolean; socialProofMessages: string; backToTop: boolean; scrollProgress: boolean; scrollProgressColor: string; pagePreloader: boolean; customCursor: boolean; customCursorColor: string; pageTransition: string; siteLanguage: string; additionalLanguages: string[]; pwaEnabled: boolean; pwaName: string; pwaShortName: string; pwaThemeColor: string; pwaBackgroundColor: string; clarityProjectId: string; hotjarSiteId: string; plausibleDomain: string; matomoUrl: string; matomoSiteId: string; googleVerification: string; yandexVerification: string; bingVerification: string; publiclyListed: boolean; publicListingTags: string[] }>) =>
    api.patch<ApiSite>(`/sites/${id}`, data),
  delete: (id: string) => api.delete(`/sites/${id}`),
  getPages: (id: string) => api.get<ApiPageMeta[]>(`/sites/${id}/pages`),
  getPageContent: (siteId: string, pageId: string) => api.get<ApiPage>(`/sites/${siteId}/pages/${pageId}`),
  clone: (id: string) => api.post<ApiSite>(`/sites/${id}/clone`),
  publishAsTemplate: (id: string, tags?: string[]) => api.post<{ ok: boolean; message: string }>(`/sites/${id}/publish-as-template`, { tags: tags ?? [] }),
  remix: (id: string) => api.post<ApiSite>(`/sites/${id}/remix`),
  publish: (id: string) => api.post<ApiSite>(`/sites/${id}/publish`),
  unpublish: (id: string) => api.post<ApiSite>(`/sites/${id}/unpublish`),
  upsertPage: (siteId: string, page: {
    title: string; slug: string; isHome?: boolean
    seoTitle?: string; seoDesc?: string; ogImage?: string; structuredData?: string
    pageCss?: string; pageJs?: string; canonicalUrl?: string; robotsIndex?: boolean
    password?: string; cmsTemplateFor?: string
    content?: unknown
  }) => api.post<ApiPage>(`/sites/${siteId}/pages`, page),
  deletePage: (siteId: string, pageId: string) => api.delete(`/sites/${siteId}/pages/${pageId}`),
  getPlanUsage: () => api.get<{ plan: string; planName: string; limit: number; used: number; canCreate: boolean }>('/sites/plan-usage'),
  verifyDomain: (id: string) => api.get<{ status: 'ok' | 'pending' | 'not-configured'; domain: string; cname: string | null; expected: string; verified: boolean }>(`/sites/${id}/verify-domain`),
  removeDomain: (id: string) => api.delete(`/sites/${id}/domain`),
  uploadHtml: (id: string, html: string) => api.post<{ id: string; status: string }>(`/sites/${id}/upload-html`, { html }),
  sendClientReview: (id: string, data: { clientEmail: string; note?: string; previewUrl: string }) =>
    api.post<{ ok: boolean }>(`/sites/${id}/client-review`, data),
}

// ── Media uploads ────────────────────────────────────────────────────────────
export const mediaApi = {
  upload: async (file: File): Promise<string> => {
    const fd = new FormData()
    fd.append('file', file)
    const res = await fetch(`${BASE}/media/upload`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${localStorage.getItem('bst-token') ?? ''}` },
      body: fd,
    })
    if (!res.ok) {
      const d = await res.json().catch(() => ({})) as Record<string, unknown>
      throw new Error(String(d.message ?? `HTTP ${res.status}`))
    }
    const data = await res.json() as { url: string }
    return data.url
  },
}

// ── Forms / Submissions ───────────────────────────────────────────────────────

export interface ApiSubmission {
  id: string
  formId: string
  pageSlug: string
  data: Record<string, unknown>
  ipHash?: string
  createdAt: string
}

export const formsApi = {
  list: (siteId: string, formId?: string) =>
    api.get<ApiSubmission[]>(`/sites/${siteId}/submissions${formId ? `?formId=${formId}` : ''}`),
  delete: (siteId: string, id: string) =>
    api.delete(`/sites/${siteId}/submissions/${id}`),
}

// ── CMS ───────────────────────────────────────────────────────────────────────

export interface ApiCollection {
  id: string
  name: string
  slug: string
  schema: Array<{ name: string; type: string; required?: boolean }>
  createdAt: string
}

export interface ApiCollectionItem {
  id: string
  data: Record<string, unknown>
  published: boolean
  createdAt: string
  updatedAt: string
}

// ── Ecommerce ─────────────────────────────────────────────────────────────────

export interface ApiProduct {
  id: string
  name: string
  slug: string
  description?: string
  price: string
  compareAt?: string
  stock: number
  images: string[]
  category?: string
  published: boolean
  isDigital?: boolean
  digitalFileUrl?: string
  createdAt: string
}

export interface ApiOrder {
  id: string
  number: number
  status: string
  customerName?: string
  customerEmail?: string
  customerPhone?: string
  total: string
  createdAt: string
  items: Array<{ id: string; name: string; price: string; quantity: number }>
}

export const ecomApi = {
  listProducts: (siteId: string, opts?: { published?: boolean; category?: string }) => {
    const qs = new URLSearchParams()
    if (opts?.published !== undefined) qs.set('published', String(opts.published))
    if (opts?.category) qs.set('category', opts.category)
    const q = qs.toString()
    return api.get<ApiProduct[]>(`/sites/${siteId}/products${q ? `?${q}` : ''}`)
  },
  createProduct: (siteId: string, data: Partial<ApiProduct> & { price: number }) =>
    api.post<ApiProduct>(`/sites/${siteId}/products`, data),
  updateProduct: (siteId: string, id: string, data: Partial<ApiProduct & { price: number }>) =>
    api.patch<ApiProduct>(`/sites/${siteId}/products/${id}`, data),
  deleteProduct: (siteId: string, id: string) => api.delete(`/sites/${siteId}/products/${id}`),
  listOrders: (siteId: string, status?: string) =>
    api.get<ApiOrder[]>(`/sites/${siteId}/orders${status ? `?status=${status}` : ''}`),
  getOrder: (siteId: string, id: string) => api.get<ApiOrder>(`/sites/${siteId}/orders/${id}`),
  updateOrderStatus: (siteId: string, id: string, status: string) =>
    api.patch<ApiOrder>(`/sites/${siteId}/orders/${id}/status`, { status }),
}

// ── Promo Codes ───────────────────────────────────────────────────────────────

export interface ApiPromoCode {
  id: string
  code: string
  type: 'PERCENT' | 'FIXED'
  value: string
  minOrder?: string
  usageLimit?: number
  usedCount: number
  expiresAt?: string
  active: boolean
  createdAt: string
}

export interface ApiShippingZone {
  id: string
  name: string
  regions: string[]
  flatRate: string
  freeAbove?: string | null
  active: boolean
  createdAt: string
}

export const shippingApi = {
  list: (siteId: string) => api.get<ApiShippingZone[]>(`/sites/${siteId}/shipping`),
  create: (siteId: string, data: { name: string; regions?: string[]; flatRate?: number; freeAbove?: number }) =>
    api.post<ApiShippingZone>(`/sites/${siteId}/shipping`, data),
  update: (siteId: string, id: string, data: { name?: string; regions?: string[]; flatRate?: number; freeAbove?: number | null; active?: boolean }) =>
    api.patch<ApiShippingZone>(`/sites/${siteId}/shipping/${id}`, data),
  delete: (siteId: string, id: string) => api.delete(`/sites/${siteId}/shipping/${id}`),
}

export const promoApi = {
  list: (siteId: string) => api.get<ApiPromoCode[]>(`/sites/${siteId}/promo-codes`),
  create: (siteId: string, data: { code: string; type: 'PERCENT' | 'FIXED'; value: number; minOrder?: number; usageLimit?: number; expiresAt?: string }) =>
    api.post<ApiPromoCode>(`/sites/${siteId}/promo-codes`, data),
  update: (siteId: string, id: string, data: { active?: boolean; usageLimit?: number; expiresAt?: string }) =>
    api.patch<ApiPromoCode>(`/sites/${siteId}/promo-codes/${id}`, data),
  delete: (siteId: string, id: string) => api.delete(`/sites/${siteId}/promo-codes/${id}`),
}

// ── Analytics ─────────────────────────────────────────────────────────────────

export interface ApiAnalyticsStats {
  total: number
  byPage: Array<{ slug: string; views: number }>
  byDay: Array<{ day: string; views: number }>
  byReferer: Array<{ referer: string | null; views: number }>
}

export const analyticsApi = {
  getStats: (siteId: string, days = 30) =>
    api.get<ApiAnalyticsStats>(`/sites/${siteId}/analytics?days=${days}`),
}

// ── SEO ───────────────────────────────────────────────────────────────────────

export interface ApiSeoIssue {
  type: 'error' | 'warning' | 'info'
  rule: string
  message: string
  page?: string
}

export interface ApiSeoReport {
  score: number
  issues: ApiSeoIssue[]
  pageCount: number
  checkedAt: string
}

export const seoApi = {
  audit: (siteId: string) => api.get<ApiSeoReport>(`/sites/${siteId}/seo/audit`),
  sitemapUrl: (siteId: string) => `/api/sites/${siteId}/seo/sitemap`,
}

// ── A/B Tests ─────────────────────────────────────────────────────────────────

export interface ApiAbTest {
  id: string
  name: string
  pageSlug: string
  blockId: string
  status: 'RUNNING' | 'PAUSED' | 'ENDED'
  createdAt: string
  stats: {
    A: { views: number; conversions: number; rate: number }
    B: { views: number; conversions: number; rate: number }
  }
}

export const abTestsApi = {
  list: (siteId: string) => api.get<ApiAbTest[]>(`/sites/${siteId}/ab-tests`),
  create: (siteId: string, data: { name: string; pageSlug: string; blockId: string }) =>
    api.post<ApiAbTest>(`/sites/${siteId}/ab-tests`, data),
  updateStatus: (siteId: string, testId: string, status: string) =>
    api.patch<ApiAbTest>(`/sites/${siteId}/ab-tests/${testId}/status`, { status }),
  delete: (siteId: string, testId: string) => api.delete(`/sites/${siteId}/ab-tests/${testId}`),
}

// ── Redirects ─────────────────────────────────────────────────────────────────

export interface ApiRedirect {
  id: string
  from: string
  to: string
  permanent: boolean
  active: boolean
  createdAt: string
}

export const redirectsApi = {
  list: (siteId: string) => api.get<ApiRedirect[]>(`/sites/${siteId}/redirects`),
  create: (siteId: string, data: { from: string; to: string; permanent?: boolean }) =>
    api.post<ApiRedirect>(`/sites/${siteId}/redirects`, data),
  update: (siteId: string, id: string, data: { to?: string; permanent?: boolean; active?: boolean }) =>
    api.patch<ApiRedirect>(`/sites/${siteId}/redirects/${id}`, data),
  delete: (siteId: string, id: string) => api.delete(`/sites/${siteId}/redirects/${id}`),
}

// ── Workspaces ────────────────────────────────────────────────────────────────

export interface ApiWorkspace {
  id: string
  name: string
  slug: string
  plan: string
  whitelabelEnabled: boolean
  whitelabelName?: string
  whitelabelLogo?: string
  whitelabelColor?: string
  whitelabelDomain?: string
  createdAt: string
  _count?: { members: number; sites: number }
}

export interface ApiWorkspaceMember {
  id: string
  role: 'OWNER' | 'ADMIN' | 'EDITOR' | 'VIEWER'
  userId: string
  workspaceId: string
  createdAt: string
  user?: { id: string; name?: string; email: string; avatarUrl?: string }
  workspace?: ApiWorkspace
}

export const workspacesApi = {
  list: () => api.get<ApiWorkspaceMember[]>('/workspaces'),
  create: (name: string, slug: string) => api.post<ApiWorkspace>('/workspaces', { name, slug }),
  get: (id: string) => api.get<ApiWorkspace & { members: ApiWorkspaceMember[] }>(`/workspaces/${id}`),
  update: (id: string, data: Partial<Pick<ApiWorkspace, 'name' | 'whitelabelEnabled' | 'whitelabelName' | 'whitelabelLogo' | 'whitelabelColor' | 'whitelabelDomain'>>) =>
    api.patch<ApiWorkspace>(`/workspaces/${id}`, data),
  delete: (id: string) => api.delete(`/workspaces/${id}`),
  invite: (id: string, email: string, role: 'ADMIN' | 'EDITOR' | 'VIEWER') =>
    api.post<ApiWorkspaceMember>(`/workspaces/${id}/members`, { email, role }),
  removeMember: (id: string, userId: string) => api.delete(`/workspaces/${id}/members/${userId}`),
  updateRole: (id: string, userId: string, role: 'ADMIN' | 'EDITOR' | 'VIEWER') =>
    api.patch<ApiWorkspaceMember>(`/workspaces/${id}/members/${userId}/role`, { role }),
}

// ── Custom Blocks ─────────────────────────────────────────────────────────────

export interface ApiCustomBlock {
  id: string
  name: string
  icon: string
  description?: string
  category: string
  html: string
  css: string
  js: string
  fields: Array<{ name: string; label: string; default: string }>
  public: boolean
  createdAt: string
}

export const customBlocksApi = {
  list: () => api.get<ApiCustomBlock[]>('/custom-blocks'),
  marketplace: () => api.get<ApiCustomBlock[]>('/custom-blocks/marketplace'),
  get: (id: string) => api.get<ApiCustomBlock>(`/custom-blocks/${id}`),
  create: (data: { name: string; icon?: string; description?: string; html: string; css?: string; js?: string; fields?: ApiCustomBlock['fields']; public?: boolean }) =>
    api.post<ApiCustomBlock>('/custom-blocks', data),
  update: (id: string, data: Partial<Omit<ApiCustomBlock, 'id' | 'createdAt'>>) =>
    api.patch<ApiCustomBlock>(`/custom-blocks/${id}`, data),
  delete: (id: string) => api.delete(`/custom-blocks/${id}`),
}

// ── Webhooks ──────────────────────────────────────────────────────────────────

export interface ApiWebhook {
  id: string
  url: string
  events: string[]
  secret?: string
  active: boolean
  createdAt: string
}

export const webhooksApi = {
  list: (siteId: string) => api.get<ApiWebhook[]>(`/sites/${siteId}/webhooks`),
  create: (siteId: string, data: { url: string; events: string[]; secret?: string }) =>
    api.post<ApiWebhook>(`/sites/${siteId}/webhooks`, data),
  update: (siteId: string, id: string, data: { url?: string; events?: string[]; secret?: string; active?: boolean }) =>
    api.patch<ApiWebhook>(`/sites/${siteId}/webhooks/${id}`, data),
  delete: (siteId: string, id: string) => api.delete(`/sites/${siteId}/webhooks/${id}`),
  events: () => api.get<string[]>('/webhooks/events'),
}

export interface ApiInboundWebhook {
  id: string
  name: string
  token: string
  action: string
  collectionSlug?: string
  fieldMap?: Record<string, string>
  active: boolean
  receivedAt?: string
  createdAt: string
}

export const inboundWebhooksApi = {
  list: (siteId: string) => api.get<ApiInboundWebhook[]>(`/sites/${siteId}/webhooks/inbound`),
  create: (siteId: string, data: { name: string; action: string; collectionSlug?: string; fieldMap?: Record<string, string> }) =>
    api.post<ApiInboundWebhook>(`/sites/${siteId}/webhooks/inbound`, data),
  delete: (siteId: string, id: string) => api.delete(`/sites/${siteId}/webhooks/inbound/${id}`),
}

// ── API Keys ──────────────────────────────────────────────────────────────────

export interface ApiKey {
  id: string
  name: string
  key: string
  lastUsed?: string
  expiresAt?: string
  createdAt: string
}

export const apiKeysApi = {
  list: () => api.get<ApiKey[]>('/api-keys'),
  create: (name: string, expiresAt?: string) => api.post<ApiKey>('/api-keys', { name, expiresAt }),
  revoke: (id: string) => api.delete(`/api-keys/${id}`),
}

// ── Saved Blocks (Reusable Components) ───────────────────────────────────────

export interface ApiSavedBlock {
  id: string
  name: string
  icon: string
  blockType: string
  data: Record<string, unknown>
  createdAt: string
}

export const savedBlocksApi = {
  list: () => api.get<ApiSavedBlock[]>('/saved-blocks'),
  create: (data: { name: string; icon?: string; blockType: string; data: Record<string, unknown> }) =>
    api.post<ApiSavedBlock>('/saved-blocks', data),
  delete: (id: string) => api.delete(`/saved-blocks/${id}`),
}

export interface ApiSiteMember {
  id: string
  siteId: string
  email: string
  role: 'EDITOR' | 'VIEWER'
  inviteToken: string | null
  accepted: boolean
  acceptedAt: string | null
  createdAt: string
}

export const siteMembersApi = {
  list: (siteId: string) => api.get<ApiSiteMember[]>(`/sites/${siteId}/members`),
  invite: (siteId: string, email: string, role?: 'EDITOR' | 'VIEWER') =>
    api.post<ApiSiteMember>(`/sites/${siteId}/members`, { email, role }),
  remove: (siteId: string, memberId: string) => api.delete(`/sites/${siteId}/members/${memberId}`),
  acceptInvite: (token: string) => api.post<ApiSiteMember>(`/sites/join/${token}`),
  getSharedSites: () => api.get<(ApiSite & { memberRole: 'EDITOR' | 'VIEWER' })[]>('/sites/shared'),
}

export interface PageTranslation {
  title?: string; seoTitle?: string; seoDesc?: string; sections?: unknown[]
}

export const translationsApi = {
  get: (siteId: string, pageSlug: string) =>
    api.get<{ translations: Record<string, PageTranslation> }>(`/sites/${siteId}/translations?slug=${encodeURIComponent(pageSlug)}`),
  save: (siteId: string, pageSlug: string, lang: string, data: PageTranslation) =>
    api.put<{ ok: boolean; lang: string }>(`/sites/${siteId}/translations/${lang}?slug=${encodeURIComponent(pageSlug)}`, data),
  delete: (siteId: string, pageSlug: string, lang: string) =>
    api.delete(`/sites/${siteId}/translations/${lang}?slug=${encodeURIComponent(pageSlug)}`),
}

export const aiTranslateApi = {
  translatePage: (content: { sections: unknown[] }, targetLang: string) =>
    api.post<{ sections: unknown[] }>('/ai/translate', { content, targetLang }),
}

export const cmsApi = {
  listCollections: (siteId: string) =>
    api.get<ApiCollection[]>(`/sites/${siteId}/collections`),
  createCollection: (siteId: string, name: string, slug: string, schema?: ApiCollection['schema']) =>
    api.post<ApiCollection>(`/sites/${siteId}/collections`, { name, slug, schema: schema ?? [] }),
  updateCollection: (siteId: string, id: string, schema: ApiCollection['schema']) =>
    api.patch<ApiCollection>(`/sites/${siteId}/collections/${id}`, { schema }),
  deleteCollection: (siteId: string, id: string) =>
    api.delete(`/sites/${siteId}/collections/${id}`),
  listItems: (siteId: string, collectionId: string) =>
    api.get<ApiCollectionItem[]>(`/sites/${siteId}/collections/${collectionId}/items`),
  createItem: (siteId: string, collectionId: string, data: Record<string, unknown>) =>
    api.post<ApiCollectionItem>(`/sites/${siteId}/collections/${collectionId}/items`, { data }),
  updateItem: (siteId: string, collectionId: string, itemId: string, data: Record<string, unknown>, published?: boolean) =>
    api.patch<ApiCollectionItem>(`/sites/${siteId}/collections/${collectionId}/items/${itemId}`, { data, published }),
  deleteItem: (siteId: string, collectionId: string, itemId: string) =>
    api.delete(`/sites/${siteId}/collections/${collectionId}/items/${itemId}`),
  bulkImport: (siteId: string, collectionId: string, items: Record<string, unknown>[]) =>
    api.post<{ created: number }>(`/sites/${siteId}/collections/${collectionId}/items/bulk-import`, { items }),
}


export interface ApiCanvasComment {
  id: string
  siteId: string
  pageSlug: string
  userId: string
  userName: string
  text: string
  x: number
  y: number
  resolved: boolean
  createdAt: string
}

export const canvasCommentsApi = {
  list: (siteId: string, pageSlug?: string) =>
    api.get<ApiCanvasComment[]>(`/sites/${siteId}/comments${pageSlug ? `?slug=${encodeURIComponent(pageSlug)}` : ''}`),
  create: (siteId: string, data: { pageSlug: string; text: string; x: number; y: number }) =>
    api.post<ApiCanvasComment>(`/sites/${siteId}/comments`, data),
  resolve: (siteId: string, id: string) =>
    api.patch<ApiCanvasComment>(`/sites/${siteId}/comments/${id}/resolve`, {}),
  delete: (siteId: string, id: string) =>
    api.delete(`/sites/${siteId}/comments/${id}`),
}

// ── Newsletter ────────────────────────────────────────────────────────────────

export interface ApiNewsletterSubscriber {
  id: string
  email: string
  name?: string
  createdAt: string
}

export const newsletterApi = {
  list: (siteId: string) => api.get<ApiNewsletterSubscriber[]>(`/sites/${siteId}/newsletter/subscribers`),
  delete: (siteId: string, id: string) => api.delete(`/sites/${siteId}/newsletter/subscribers/${id}`),
}

// ── Public Gallery ────────────────────────────────────────────────────────────

export interface ApiGalleryItem {
  id: string
  name: string
  slug: string
  favicon?: string
  publicListingTags: string[]
  publishedAt?: string
}

export interface ApiGalleryResult {
  items: ApiGalleryItem[]
  total: number
  page: number
  pages: number
}

export const galleryApi = {
  list: (opts?: { page?: number; tag?: string; q?: string }) => {
    const qs = new URLSearchParams()
    if (opts?.page) qs.set('page', String(opts.page))
    if (opts?.tag) qs.set('tag', opts.tag)
    if (opts?.q) qs.set('q', opts.q)
    const q = qs.toString()
    return fetch(`${(import.meta.env.VITE_API_URL ?? '') + '/api'}/public/sites${q ? `?${q}` : ''}`)
      .then(r => r.json() as Promise<ApiGalleryResult>)
  },
}

// ── Cloud ─────────────────────────────────────────────────────────────────────

export interface CloudStatus {
  id: string
  userId: string
  plan: string
  subdomain: string
  status: string
  diskUsedMb: number
  url: string
  createdAt: string
  specs: { cpus: number; memoryGb: number; diskGb: number; bandwidth: string } | null
}

export interface CloudFile {
  name: string
  size: number
  mtime: string
}

// ── Hosting ───────────────────────────────────────────────────────────────────

export interface ApiHostingProject {
  id: string
  userId: string
  name: string
  framework: string
  repoUrl: string | null
  repoBranch: string
  buildCmd: string
  outputDir: string
  installCmd: string
  nodeVersion: string
  webhookToken: string
  createdAt: string
  updatedAt: string
  _count?: { deployments: number }
  latestDeployment?: ApiHostingDeployment | null
}

export interface ApiHostingDeployment {
  id: string
  projectId: string
  branch: string
  commitSha: string | null
  commitMsg: string | null
  status: 'pending' | 'building' | 'success' | 'failed' | 'cancelled'
  buildLog?: string | null
  outputPath?: string | null
  previewUrl: string | null
  isProduction: boolean
  triggeredBy: string
  startedAt: string | null
  finishedAt: string | null
  createdAt: string
}

export const hostingApi = {
  listProjects: () =>
    api.get<ApiHostingProject[]>('/hosting/projects'),

  createProject: (dto: { name: string; repoUrl?: string; repoBranch?: string; buildCmd?: string; outputDir?: string; installCmd?: string; framework?: string }) =>
    api.post<ApiHostingProject>('/hosting/projects', dto),

  getProject: (id: string) =>
    api.get<ApiHostingProject>(`/hosting/projects/${id}`),

  updateProject: (id: string, dto: Partial<{ name: string; repoUrl: string; repoBranch: string; buildCmd: string; outputDir: string; installCmd: string; framework: string }>) =>
    api.patch<ApiHostingProject>(`/hosting/projects/${id}`, dto),

  deleteProject: (id: string) =>
    api.delete(`/hosting/projects/${id}`),

  getEnvKeys: (id: string) =>
    api.get<string[]>(`/hosting/projects/${id}/env`),

  setEnvVar: (id: string, key: string, value: string) =>
    api.post<{ ok: boolean }>(`/hosting/projects/${id}/env`, { key, value }),

  deleteEnvVar: (id: string, key: string) =>
    api.delete(`/hosting/projects/${id}/env/${encodeURIComponent(key)}`),

  listDeployments: (id: string) =>
    api.get<ApiHostingDeployment[]>(`/hosting/projects/${id}/deployments`),

  getDeployment: (deploymentId: string) =>
    api.get<ApiHostingDeployment>(`/hosting/deployments/${deploymentId}`),

  deployGit: (id: string, repoUrl?: string) =>
    api.post<ApiHostingDeployment>(`/hosting/projects/${id}/deploy/git`, { repoUrl }),

  deployZip: (id: string, file: File, commitMsg?: string): Promise<ApiHostingDeployment> => {
    const form = new FormData()
    form.append('file', file)
    if (commitMsg) form.append('commitMsg', commitMsg)
    const token = localStorage.getItem('bst-token')
    return fetch(`${BASE}/hosting/projects/${id}/deploy/zip`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: form,
    }).then(async r => {
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error((e as { message?: string }).message ?? 'Upload failed') }
      return r.json() as Promise<ApiHostingDeployment>
    })
  },
}

// ── §62 Website Score ─────────────────────────────────────────────────────────

export interface ApiWebsiteScoreInput {
  siteName: string
  pagesCount: number
  pages: Array<{ title: string; slug: string; sections: Array<{ type?: string; hasTitle?: boolean; hasCta?: boolean; hasImage?: boolean; hasForm?: boolean }> }>
  hasCms?: boolean
  hasAnalytics?: boolean
  hasCustomDomain?: boolean
  themeConfig?: string
}

export interface ApiWebsiteScoreResult {
  overallScore: number
  grade: string
  metrics: { visual_design: number; ux: number; responsive: number; accessibility: number; seo: number; performance: number; consistency: number; functionality: number; security: number }
  verdict: string
  issues: Array<{ category: string; severity: string; title: string; description: string; fix: string }>
  topPriorities: string[]
}

export interface ApiQaAgentInput {
  siteName: string
  pages: Array<{ title: string; slug: string; blocks: Array<{ type: string; hasCta?: boolean; hasForm?: boolean; formFields?: string[] }> }>
}

export interface ApiQaAgentResult {
  passedTests: number
  failedTests: number
  overallResult: string
  summary: string
  issues: Array<{ test: string; severity: string; page?: string; description: string; fixSuggestion: string }>
}

export interface ApiWebsiteDoctorInput {
  siteName: string
  pagesCount: number
  pages: Array<{ title: string; slug: string; blockTypes: string[]; seoTitle?: string; seoDesc?: string }>
  hasAnalytics: boolean
  hasCms: boolean
  hasEcommerce: boolean
  hasCustomDomain: boolean
  hasForms: boolean
}

export interface ApiWebsiteDoctorResult {
  healthScore: number
  grade: string
  summary: string
  dimensions: Array<{ name: string; score: number; issues: Array<{ severity: string; title: string; description: string; fix: string }> }>
  criticalCount: number
  highCount: number
  mediumCount: number
  lowCount: number
  topPriorities: string[]
}

export interface ApiNaturalLanguageEditInput {
  instruction: string
  siteName?: string
  themeConfig?: string
  pages: Array<{ pageId: string; sections: Array<{ anchorId: string; blocks: Array<{ id: string; type: string; textFields: Record<string, string> }> }> }>
}

export interface ApiNaturalLanguageEditResult {
  plan: string
  summary: string
  themeChanges?: Record<string, string>
  blockChanges: Array<{ pageId: string; anchorId: string; blockId: string; changes: Record<string, unknown> }>
}

export interface ApiProjectPlanResult {
  title: string
  summary: string
  estimatedTime: string
  steps: Array<{ step: number; action: string; detail: string }>
  warnings: string[]
  requiresApproval: boolean
}

export interface ApiGlobalSearchResult {
  type: 'site' | 'page' | 'cms' | 'media'
  id: string
  title: string
  subtitle: string
  siteId?: string
  url: string
}

export const aiAnalyticsApi = {
  websiteScore: (input: ApiWebsiteScoreInput) =>
    api.post<ApiWebsiteScoreResult>('/ai/website-score', { input }),
  qaAgent: (input: ApiQaAgentInput) =>
    api.post<ApiQaAgentResult>('/ai/qa-agent', { input }),
  websiteDoctor: (input: ApiWebsiteDoctorInput) =>
    api.post<ApiWebsiteDoctorResult>('/ai/website-doctor', { input }),
  naturalLanguageEdit: (input: ApiNaturalLanguageEditInput) =>
    api.post<ApiNaturalLanguageEditResult>('/ai/natural-language-edit', { input }),
  projectPlan: (prompt: string, pagesCount?: number, existingBlocks?: string[]) =>
    api.post<ApiProjectPlanResult>('/ai/project-plan', { prompt, pagesCount, existingBlocks }),
  explain: (query: string, context: { siteName: string; pagesCount: number; pages: Array<{ title: string; slug: string; blockTypes: string[] }> }) =>
    api.post<{ answer: string; references: string[] }>('/ai/explain', { query, context }),
}

export const searchApi = {
  global: (q: string, siteId?: string) =>
    api.get<ApiGlobalSearchResult[]>(`/search?q=${encodeURIComponent(q)}${siteId ? `&siteId=${siteId}` : ''}`),
}

// ── Figma ─────────────────────────────────────────────────────────────────────

export interface ApiFigmaConnection {
  connected: boolean
  userHandle: string | null
  userEmail: string | null
}

export interface ApiFigmaImport {
  id: string
  figmaFileName: string | null
  figmaUrl: string
  status: string
  progress: number
  currentStep: string | null
  errorMsg: string | null
  siteId: string | null
  createdAt: string
  updatedAt: string
}

export interface ApiFigmaImportDetail extends ApiFigmaImport {
  figmaIR: unknown
  designSystem: {
    colors: Array<{ name: string; value: string; group?: string }>
    typography: Array<{ name: string; fontFamily: string; fontWeight: number; fontSize: number; lineHeight: string }>
    fonts: string[]
    shadows: Array<{ name: string; value: string }>
  } | null
  generatedPages: Array<{
    name: string
    figmaFrameId: string
    slug: string
    fullHtml: string
    designTokensCss: string
    components: Array<{ name: string; componentType: string }>
  }> | null
}

export interface ApiFigmaFileInfo {
  fileKey: string
  fileName: string
  lastModified: string
  thumbnailUrl: string | null
  pages: Array<{ id: string; name: string }>
}

export const figmaApi = {
  getConnection: () =>
    api.get<ApiFigmaConnection | null>('/figma/connection'),

  connect: (pat: string) =>
    api.post<{ handle: string; email: string }>('/figma/connection', { pat }),

  disconnect: () =>
    api.delete('/figma/connection'),

  getFileInfo: (figmaUrl: string) =>
    api.post<ApiFigmaFileInfo>('/figma/file-info', { figmaUrl }),

  listImports: () =>
    api.get<ApiFigmaImport[]>('/figma/imports'),

  startImport: (figmaUrl: string, siteId?: string) =>
    api.post<{ importId: string }>('/figma/imports', { figmaUrl, siteId }),

  getImport: (importId: string) =>
    api.get<ApiFigmaImportDetail>('/figma/imports/' + importId),

  deleteImport: (importId: string) =>
    api.delete('/figma/imports/' + importId),
}

// ── Backend Builder (§40+§41+§44) ────────────────────────────────────────────

export interface ApiDbColumn {
  id: string; tableId: string; name: string; type: string
  nullable: boolean; isUnique: boolean; isPrimary: boolean
  defaultVal: string | null; references: string | null; position: number; createdAt: string
}

export interface ApiDbTable {
  id: string; backendId: string; name: string; displayName: string | null
  columns: ApiDbColumn[]; createdAt: string; updatedAt: string
}

export interface ApiApiEndpoint {
  id: string; backendId: string; method: string; path: string
  description: string | null; handler: string | null; auth: string
  isEnabled: boolean; createdAt: string; updatedAt: string
}

export interface ApiUserBackend {
  id: string; userId: string; name: string; description: string | null
  tables: ApiDbTable[]; endpoints: ApiApiEndpoint[]
  _count?: { tables: number; endpoints: number }
  createdAt: string; updatedAt: string
}

export const backendBuilderApi = {
  list: () => api.get<ApiUserBackend[]>('/backend-builder'),
  create: (name: string, description?: string) => api.post<ApiUserBackend>('/backend-builder', { name, description }),
  get: (id: string) => api.get<ApiUserBackend>(`/backend-builder/${id}`),
  update: (id: string, dto: { name?: string; description?: string }) => api.patch<ApiUserBackend>(`/backend-builder/${id}`, dto),
  remove: (id: string) => api.delete(`/backend-builder/${id}`),
  schemaSql: (id: string) => api.get<{ sql: string }>(`/backend-builder/${id}/schema.sql`),

  addTable: (id: string, name: string, displayName?: string) =>
    api.post<ApiDbTable>(`/backend-builder/${id}/tables`, { name, displayName }),
  updateTable: (id: string, tableId: string, dto: { name?: string; displayName?: string }) =>
    api.patch<ApiDbTable>(`/backend-builder/${id}/tables/${tableId}`, dto),
  removeTable: (id: string, tableId: string) =>
    api.delete(`/backend-builder/${id}/tables/${tableId}`),

  addColumn: (id: string, tableId: string, dto: Partial<ApiDbColumn> & { name: string }) =>
    api.post<ApiDbColumn>(`/backend-builder/${id}/tables/${tableId}/columns`, dto),
  updateColumn: (id: string, tableId: string, columnId: string, dto: Partial<ApiDbColumn>) =>
    api.patch<ApiDbColumn>(`/backend-builder/${id}/tables/${tableId}/columns/${columnId}`, dto),
  removeColumn: (id: string, tableId: string, columnId: string) =>
    api.delete(`/backend-builder/${id}/tables/${tableId}/columns/${columnId}`),

  addEndpoint: (id: string, dto: Partial<ApiApiEndpoint> & { path: string }) =>
    api.post<ApiApiEndpoint>(`/backend-builder/${id}/endpoints`, dto),
  updateEndpoint: (id: string, endpointId: string, dto: Partial<ApiApiEndpoint>) =>
    api.patch<ApiApiEndpoint>(`/backend-builder/${id}/endpoints/${endpointId}`, dto),
  removeEndpoint: (id: string, endpointId: string) =>
    api.delete(`/backend-builder/${id}/endpoints/${endpointId}`),
}

// ── §Project Engine Phase B — Projects API ───────────────────────────────────

export type ProjectType = 'WEBSITE' | 'HOSTING' | 'BACKEND' | 'FULLSTACK' | 'MOBILE'
export type ProjectStatus =
  | 'DRAFT' | 'PUBLISHED' | 'ARCHIVED' | 'SUSPENDED'
  | 'BUILDING' | 'PREVIEW' | 'DEPLOYING' | 'LIVE' | 'FAILED'
export type ProjectSource = 'EDITOR' | 'CODE_IMPORT' | 'AI_GENERATED' | 'FIGMA_IMPORT' | 'TEMPLATE'

export interface ApiProject {
  id: string
  name: string
  slug: string
  type: ProjectType
  source: ProjectSource
  status: ProjectStatus
  description?: string | null
  thumbnailUrl?: string | null
  ownerId: string
  workspaceId?: string | null
  siteId?: string | null
  hostingProjectId?: string | null
  userBackendId?: string | null
  createdAt: string
  updatedAt: string
}

export interface ApiProjectDetail extends ApiProject {
  site?: {
    themeConfig?: string | null
    customHeadHtml?: string | null
    customBodyHtml?: string | null
    gaId?: string | null
    ymId?: string | null
    fbPixelId?: string | null
    vkPixelId?: string | null
    gtmId?: string | null
    cookieConsent?: boolean
    pwaEnabled?: boolean
    globalHeader?: string | null
    globalFooter?: string | null
    customDomain?: string | null
    customDomainVerified?: boolean
    publishedAt?: string | null
    status?: string
  } | null
  hostingProject?: {
    buildCmd?: string | null
    outputDir?: string | null
    framework?: string | null
    repoUrl?: string | null
    repoBranch?: string | null
  } | null
}

export interface ApiProjectSummary extends ApiProject {
  _count: { operations: number }
}

export interface ApiProjectList {
  items: ApiProject[]
  hasMore: boolean
  nextCursor: string | null
}

export interface ApiProjectPage {
  id: string
  title: string
  slug: string
  isHome: boolean
  seoTitle?: string | null
  seoDesc?: string | null
  ogImage?: string | null
  pageCss?: string | null
  pageJs?: string | null
  updatedAt: string
  createdAt: string
}

export interface ApiProjectPageContent extends ApiProjectPage {
  content: { sections?: unknown[] }
}

export interface ApiProjectOperation {
  id: string
  type: string
  actorType: string
  actorId: string | null
  payload: unknown
  createdAt: string
}

export interface ApiComponent {
  id: string
  projectId: string
  name: string
  description: string | null
  category: string
  tag: string | null
  templateData: { blockType?: string; blockData?: unknown }
  thumbnailUrl: string | null
  isShared: boolean
  currentVersionId: string | null
  createdAt: string
  updatedAt: string
}

export const projectsApi = {
  // List
  list: (cursor?: string, limit?: number) => {
    const qs = new URLSearchParams()
    if (cursor) qs.set('cursor', cursor)
    if (limit) qs.set('limit', String(limit))
    const q = qs.toString()
    return api.get<ApiProjectList>(`/projects${q ? `?${q}` : ''}`)
  },

  listByWorkspace: (workspaceId: string, cursor?: string, limit?: number) => {
    const qs = new URLSearchParams()
    if (cursor) qs.set('cursor', cursor)
    if (limit) qs.set('limit', String(limit))
    const q = qs.toString()
    return api.get<ApiProjectList>(`/projects/workspace/${workspaceId}${q ? `?${q}` : ''}`)
  },

  // Get
  get: (id: string) => api.get<ApiProjectDetail>(`/projects/${id}`),
  getMetadata: (id: string) => api.get<ApiProjectSummary>(`/projects/${id}/metadata`),

  // Create
  create: (dto: { name: string; type: ProjectType; slug?: string; workspaceId?: string; description?: string; siteId?: string; hostingProjectId?: string; userBackendId?: string }) =>
    api.post<ApiProject>('/projects', dto),

  createWebsite: (dto: { name: string; slug?: string; workspaceId?: string; description?: string; templateSourceId?: string }) =>
    api.post<ApiProject>('/projects/website', dto),

  createCodeProject: (dto: { name: string; slug?: string; workspaceId?: string; description?: string; repoUrl?: string; repoBranch?: string; buildCmd?: string; outputDir?: string; framework?: string }) =>
    api.post<ApiProject>('/projects/code', dto),

  // Update / Status
  update: (id: string, dto: { name?: string; slug?: string; status?: ProjectStatus; description?: string; thumbnailUrl?: string }) =>
    api.patch<ApiProject>(`/projects/${id}`, dto),

  publish: (id: string) =>
    api.post<ApiProjectSummary>(`/projects/${id}/publish`),

  archive: (id: string) =>
    api.post<{ status: 'ARCHIVED' }>(`/projects/${id}/archive`),

  delete: (id: string) =>
    api.delete(`/projects/${id}`),

  // Shadow creation
  shadowForSite: (siteId: string) =>
    api.post<ApiProject>(`/projects/shadow/site/${siteId}`),

  shadowForHosting: (hostingProjectId: string) =>
    api.post<ApiProject>(`/projects/shadow/hosting/${hostingProjectId}`),

  shadowForBackend: (backendId: string) =>
    api.post<ApiProject>(`/projects/shadow/backend/${backendId}`),

  // Pages
  listPages: (projectId: string) =>
    api.get<ApiProjectPage[]>(`/projects/${projectId}/pages`),

  getPage: (projectId: string, pageId: string) =>
    api.get<ApiProjectPageContent>(`/projects/${projectId}/pages/${pageId}`),

  createPage: (projectId: string, dto: { title: string; slug: string; isHome?: boolean; seoTitle?: string; seoDesc?: string; ogImage?: string; pageCss?: string; pageJs?: string }) =>
    api.post<ApiProjectPageContent>(`/projects/${projectId}/pages`, dto),

  updatePage: (projectId: string, pageId: string, dto: { title?: string; slug?: string; isHome?: boolean; seoTitle?: string; seoDesc?: string; ogImage?: string; pageCss?: string; pageJs?: string; content?: unknown }) =>
    api.patch<ApiProjectPageContent>(`/projects/${projectId}/pages/${pageId}`, dto),

  deletePage: (projectId: string, pageId: string) =>
    api.delete(`/projects/${projectId}/pages/${pageId}`),

  // Operations log
  listOperations: (projectId: string, cursor?: string, limit?: number) => {
    const qs = new URLSearchParams()
    if (cursor) qs.set('cursor', cursor)
    if (limit) qs.set('limit', String(limit))
    const q = qs.toString()
    return api.get<ApiProjectOperation[]>(`/projects/${projectId}/operations${q ? `?${q}` : ''}`)
  },

  // Components
  listComponents: (projectId: string) =>
    api.get<ApiComponent[]>(`/projects/${projectId}/components`),

  createComponent: (projectId: string, dto: { name: string; description?: string; category?: string; tag?: string; templateData?: unknown; thumbnailUrl?: string }) =>
    api.post<ApiComponent>(`/projects/${projectId}/components`, dto),

  updateComponent: (projectId: string, componentId: string, dto: { name?: string; description?: string; category?: string; tag?: string; templateData?: unknown; thumbnailUrl?: string }) =>
    api.patch<ApiComponent>(`/projects/${projectId}/components/${componentId}`, dto),

  deleteComponent: (projectId: string, componentId: string) =>
    api.delete(`/projects/${projectId}/components/${componentId}`),
}

// ── Phase E: Design System API ────────────────────────────────────────────────

export type TokenCategory = 'COLOR' | 'TYPOGRAPHY' | 'SPACING' | 'BORDER_RADIUS' | 'SHADOW' | 'MOTION' | 'BREAKPOINT' | 'CUSTOM'

export interface ApiDesignToken {
  id: string
  designSystemId: string
  category: TokenCategory
  key: string
  value: string
  aliasOf: string | null
  description: string | null
  source: string
  exportAsCss: boolean
  createdAt: string
  updatedAt: string
}

export interface ApiDesignSystem {
  id: string
  projectId: string
  name: string
  resolvedSnapshot: Record<string, string>
  figmaFileKey: string | null
  lastSyncedAt: string | null
  createdAt: string
  updatedAt: string
  tokens: ApiDesignToken[]
}

export const designSystemApi = {
  get: (projectId: string) =>
    api.get<ApiDesignSystem>(`/projects/${projectId}/design-system`),

  update: (projectId: string, dto: { name?: string }) =>
    api.patch<ApiDesignSystem>(`/projects/${projectId}/design-system`, dto),

  createToken: (projectId: string, dto: {
    category: TokenCategory; key: string; value: string;
    aliasOf?: string; description?: string; source?: string; exportAsCss?: boolean
  }) =>
    api.post<ApiDesignToken>(`/projects/${projectId}/design-system/tokens`, dto),

  updateToken: (projectId: string, tokenId: string, dto: {
    value?: string; aliasOf?: string; description?: string; exportAsCss?: boolean
  }) =>
    api.patch<ApiDesignToken>(`/projects/${projectId}/design-system/tokens/${tokenId}`, dto),

  deleteToken: (projectId: string, tokenId: string) =>
    api.delete(`/projects/${projectId}/design-system/tokens/${tokenId}`),
}

// ── §81 Feature Flags ─────────────────────────────────────────────────────────

export interface ApiFeatureFlag {
  key: string
  enabled: boolean
  rolloutPct: number
  description: string | null
}

export const featureFlagsApi = {
  list: () => api.get<Record<string, ApiFeatureFlag>>('/feature-flags'),
  adminList: () => api.get<ApiFeatureFlag[]>('/feature-flags/admin'),
  upsert: (key: string, dto: { enabled?: boolean; rolloutPct?: number; description?: string }) =>
    api.put<ApiFeatureFlag>(`/feature-flags/admin/${key}`, dto),
  remove: (key: string) => api.delete(`/feature-flags/admin/${key}`),
}

// ── §17 Environments ──────────────────────────────────────────────────────────

export const hostingEnvApi = {
  listEnvironments: (projectId: string) =>
    api.get<Array<{ id: string; name: string; keys: string[] }>>(`/hosting/projects/${projectId}/environments`),
  setEnvVar: (projectId: string, envName: string, key: string, value: string) =>
    api.post(`/hosting/projects/${projectId}/environments/${envName}/env`, { key, value }),
  deleteEnvVar: (projectId: string, envName: string, key: string) =>
    api.delete(`/hosting/projects/${projectId}/environments/${envName}/env/${key}`),
}

// ── §96 Template / Remix System ───────────────────────────────────────────────

export interface ApiTemplate {
  id: string
  name: string
  slug: string
  templateCategory: string | null
  templateDescription: string | null
  templateThumbnail: string | null
  createdAt: string
  pages: Array<{ id: string; title: string; slug: string; isHome: boolean }>
}

export const templatesApi = {
  list: (category?: string) =>
    api.get<ApiTemplate[]>('/templates' + (category ? `?category=${category}` : '')),
  remix: (templateId: string, name: string) =>
    api.post<{ id: string; slug: string }>(`/templates/${templateId}/remix`, { name }),
}

export const siteTemplateApi = {
  saveAsTemplate: (siteId: string, dto: { category?: string; description?: string; thumbnail?: string }) =>
    api.post<{ id: string; isTemplate: boolean }>(`/sites/${siteId}/save-as-template`, dto),
}

// ── §64 Import Existing Website ───────────────────────────────────────────────

export interface ImportAnalysis {
  url: string
  title: string
  description: string
  detectedSections: string[]
  fetchError: string | null
  sectionCount: number
}

export const importSiteApi = {
  analyzeUrl: (url: string) =>
    api.post<{ analysis: ImportAnalysis; suggestedName: string; suggestedSlug: string }>('/sites/import-url', { url }),
  createFromImport: (analysis: ImportAnalysis, name: string, slug: string) =>
    api.post<{ id: string; slug: string }>('/sites/import-url/create', { analysis, name, slug }),
}

export const cloudApi = {
  status: () => api.get<CloudStatus | null>('/cloud/status'),
  files: () => api.get<CloudFile[]>('/cloud/files'),
  deleteFile: (name: string) => api.delete(`/cloud/files/${encodeURIComponent(name)}`),
  uploadFile: (file: File): Promise<{ ok: boolean; name: string; size: number }> => {
    const form = new FormData()
    form.append('file', file)
    const token = localStorage.getItem('bst-token')
    return fetch(`${BASE}/cloud/files`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: form,
    }).then(async r => {
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error((e as any).message ?? 'Upload failed') }
      return r.json()
    })
  },
}
